import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { commaSeparatedToArray } from "@unified-checkout/design-system";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { SYSTEM_EVENTS_GROUPING_CODES } from "@app/dashboard/constants/tracking_events/system";
import { FOUR_SECONDS } from "@app/dashboard/constants/shared";
import type { IField } from "@app/dashboard/types/outerLayout/common";
import type { IRoleManagementType } from "@app/dashboard/types/innerLayout/user_management";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import {
  getPermissionsCatalog
} from "@app/dashboard/services/inner_layout/user_management/roles/get-permissions-catalog";
import { createNewRole } from "@app/dashboard/services/inner_layout/user_management/roles/create-new-role";
import { getUserExistingPermissions } from "@app/dashboard/services/inner_layout/user_management/roles/get-user-existing-permissions";
import { roleUpdate } from "@app/dashboard/services/inner_layout/user_management/roles/role-update";
import { roleRetrieve } from "@app/dashboard/services/inner_layout/user_management/roles/role-retrieve";

export const useRoleManageMentAPICalls = ({
  pageType,
  fields,
  fieldsNames,
  userId,
  roleId,
  preventEdit
}: {
  pageType: IRoleManagementType,
  fields: IField,
  fieldsNames: Record<'TITLE' | 'DESCRIPTION' | 'PERMISSIONS', string>,
  userId: string,
  roleId?: string,
  preventEdit?: boolean
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [toasterInfo, setToasterInfo] = useState<{
    open: boolean,
    state: 'success' | 'error' | 'warning',
    text: string,
    hint: string,
    trackingCode?: string
  }>({
    open: false,
    state: 'success',
    text: '',
    hint: ''
  });

  useEffect(() => {
    (pageType === 'role_details' && preventEdit) &&
      setToasterInfo({
        open: true,
        state: 'warning',
        text: t('innerLayout.userManagementPage.toasters.existingPermissionsRole.text'),
        hint: t('innerLayout.userManagementPage.toasters.existingPermissionsRole.hint'),
        trackingCode: SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.EXISTING_PERMISSIONS_ROLE
      });
  }, [pageType, preventEdit]);

  const {
    isLoading: isPermsCatalogLoading,
    isError: isPermsCatalogError,
    error: permsCatalogError,
    data: permsCatalog
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_PERMS_CATALOG],
    queryFn: () => getPermissionsCatalog().then(res => res.data.data),
    enabled: pageType === 'create_new_role',
  });


  const roleDetailsQueryKey = preventEdit ?
    [`${QUERY_KEYS.GET_USER_EXISTING_PERMISSIONS}-view-only`]
    :
    [`${QUERY_KEYS.GET_USER_EXISTING_PERMISSIONS}-edit`];

  const roleDetailsQueryFn = preventEdit ?
    () => getUserExistingPermissions(userId as string).then(res => res.data.data)
    :
    () => roleRetrieve(roleId as string).then(res => res.data.data);

  const {
    isLoading: isRetrievedRoleLoading,
    isError: isRetrievedRoleError,
    error: retrievedRoleError,
    data: retrievedRole
  } = useQuery({
    queryKey: roleDetailsQueryKey,
    queryFn: roleDetailsQueryFn,
    enabled: pageType === 'role_details' && (preventEdit ? (preventEdit && !!userId) : true),
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: roleDetailsQueryKey });
    }
  }, []);


  const mutation_key = [
    pageType === 'create_new_role' ?
      QUERY_KEYS.CREATE_NEW_ROLE
      :
      QUERY_KEYS.ROLE_DETAILS
  ];

  const mutationFunction = () => {

    const queryParams =
      pageType === 'create_new_role' ?
        {
          title: fields[fieldsNames.TITLE].value,
          description: fields[fieldsNames.DESCRIPTION].value,
          permissions: commaSeparatedToArray(fields[fieldsNames.PERMISSIONS].value || '')
        } as any
        :
        {
          roleId,
          title: fields[fieldsNames.TITLE].value,
          description: fields[fieldsNames.DESCRIPTION].value,
          permissions: commaSeparatedToArray(fields[fieldsNames.PERMISSIONS].value || '')
        };

    return pageType === 'create_new_role' ?
      createNewRole(queryParams)
      :
      roleUpdate(queryParams);
  };

  const trackingCode = pageType === 'create_new_role' ?
    SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.CREATE_ROLE
    :
    SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.UPDATE_ROLE;

  const {
    isPending: isCreateOrUpdateRolePending,
    isError: isCreateOrUpdateRoleError,
    error: createOrUpdateRoleError,
    mutate: mutateCreateOrUpdateRole
  } = useMutation({
    mutationKey: mutation_key,
    mutationFn: mutationFunction,
    onSuccess: () => {

      const successText = pageType === 'create_new_role' ?
        t('innerLayout.userManagementPage.toasters.createNewRole.successText')
        :
        t('innerLayout.userManagementPage.toasters.updateRole.successText');

      const successTextHint = pageType === 'create_new_role' ?
        t('innerLayout.userManagementPage.toasters.createNewRole.successTextHint')
        :
        t('innerLayout.userManagementPage.toasters.updateRole.successTextHint', { admin: retrievedRole?.title || '' });

      setToasterInfo({
        open: true,
        state: 'success',
        text: successText,
        hint: successTextHint,
        trackingCode,
      });

      queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_ROLES] });

      setTimeout(() => {
        navigate(INNER_ROUTES.MANAGE_ROLES);
      }, FOUR_SECONDS);

    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;

      const staticErrMsg = pageType === 'create_new_role' ?
        t('innerLayout.userManagementPage.toasters.createNewRole.failureText')
        :
        t('innerLayout.userManagementPage.toasters.updateRole.failureText');

      const msg = response?.data?.message || staticErrMsg;

      setToasterInfo({
        open: true,
        state: 'error',
        text: msg,
        hint: '',
        trackingCode
      });
    },
  });

  return {
    toasterInfo,
    setToasterInfo,

    isPermsCatalogLoading,
    isPermsCatalogError,
    permsCatalogError,
    permsCatalog,

    isCreateOrUpdateRolePending,
    isCreateOrUpdateRoleError,
    createOrUpdateRoleError,
    mutateCreateOrUpdateRole,

    isRetrievedRoleLoading,
    isRetrievedRoleError,
    retrievedRoleError,
    retrievedRole
  };
}